import { jwtDecode } from 'jwt-decode';
import { HTTPContentType } from '../data/api/useApi';
import { AuthInfo } from '../data/types';
import { AuthActions } from '../redux/actions/authActions';

export const PORTAL_RETURN_PATH_KEY = 'portalReturnPath';

export interface TokenPayload {
    exp: number;
    oid?: string;
    username?: string;
}

interface OAuthResponse {
    redirectUri: string;
    idToken?: string;
}

interface AuthenticatedResponse {
    oid?: string;
}

export class AuthManager {
    private static instance: AuthManager;
    private static authActions: AuthActions | undefined;
    private authCheckPromise: Promise<string> | undefined;

    private constructor() {}

    public static getInstance(authActions?: AuthActions): AuthManager {
        if (!AuthManager.instance) {
            AuthManager.instance = new AuthManager();
        }
        AuthManager.authActions = authActions;
        return AuthManager.instance;
    }

    public signInUser(userId?: string) {
        AuthManager.authActions?.signInUser(userId);
    }

    public signOutUser() {
        AuthManager.authActions?.signOutUser();
    }

    public clearTokens() {
        localStorage.removeItem('ccid');
        this.signOutUser();
    }

    public isAccessTokenNearExpiry(bufferSeconds = 300): boolean {
        const token = localStorage.getItem('ccid');
        if (!token) {
            return true;
        }

        try {
            const payload = jwtDecode(token) as TokenPayload;
            if (!payload?.exp) {
                return true;
            }

            const millisUntilExpiry = (payload.exp * 1000) - Date.now();
            return millisUntilExpiry <= (bufferSeconds * 1000);
        } catch {
            return true;
        }
    }

    public async tryReauthenticate(): Promise<boolean> {
        try {
            await this.checkAuthentication();
            return true;
        } catch {
            return false;
        }
    }

    public async authenticate(authInfo: AuthInfo): Promise<void> {
        const response = await fetch('/api/Auth/login', {
            method: 'POST',
            headers: { 'Content-Type': HTTPContentType.json },
            body: JSON.stringify(authInfo),
        });
        if (!response.ok) {
            this.signOutUser();
            throw new Error('Authentication failed');
        }

        const authorizeResult: OAuthResponse = await response.json();
        if (!authorizeResult.redirectUri) {
            this.signOutUser();
            throw new Error('Authorization redirect URL missing');
        }

        const redirectUrl = new URL(authorizeResult.redirectUri);
        redirectUrl.searchParams.append('auth', '1');
        sessionStorage.setItem(PORTAL_RETURN_PATH_KEY, window.location.pathname);
        window.location.assign(redirectUrl.toString());
    }

    public async getAccessToken(): Promise<void> {
        const tokenResp = await fetch('/api/Auth/oauth/token', {
            method: 'POST',
            headers: { 'Content-Type': HTTPContentType.json },
        });
        if (!tokenResp.ok) {
            this.signOutUser();
            throw new Error('Access token request failed');
        }

        const accessResponse: OAuthResponse = await tokenResp.json();
        if (!accessResponse.redirectUri) {
            this.signOutUser();
            throw new Error('Authorization redirect URL missing');
        }

        const oid = accessResponse.idToken ? (jwtDecode(accessResponse.idToken) as TokenPayload).oid : undefined;
        AuthManager.authActions?.signInUser(oid);
    }

    public async checkAuthentication(): Promise<string> {
        if (this.authCheckPromise) {
            return this.authCheckPromise;
        }

        this.authCheckPromise = this.checkAuthenticationCore();

        try {
            return await this.authCheckPromise;
        } finally {
            this.authCheckPromise = undefined;
        }
    }

    private async checkAuthenticationCore(): Promise<string> {
        const authResp = await fetch('/api/Auth/oauth/authenticated', {
            method: 'GET',
            headers: { 'Content-Type': HTTPContentType.json },
        });
        if (!authResp.ok) {
            AuthManager.authActions?.signOutUser();
            throw new Error('Authentication check failed');
        }

        const authResponse: AuthenticatedResponse = await authResp.json();
        if (!authResponse.oid) {
            AuthManager.authActions?.signOutUser();
            throw new Error('Authenticated response missing oid');
        }

        AuthManager.authActions?.signInUser(authResponse.oid);
        return authResponse.oid;
    }

    public async logout(): Promise<void> {
        try {
            await fetch('/api/Auth/oauth/logout', {
                method: 'POST',
                headers: { 'Content-Type': HTTPContentType.json },
            });
        } finally {
            this.clearTokens();
        }
    }
}

export const authManager = AuthManager.getInstance();
